import { useEffect, useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, ExternalLink, Plus } from 'lucide-react';
import { toast } from 'sonner';
import type { SectionInput } from '@zt/content';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  Section,
  Select,
  Spinner,
  Switch,
  Textarea,
} from '@zt/ui';
import type { Tone } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import {
  EditorLanguages,
  LocalePicker,
  TranslationLinks,
  useContentLocale,
} from '../components/ContentLocale';
import { ContentReviewPanel, reviewTone } from '../components/ContentReview';
import { ImageUpload } from '../components/ImageUpload';
import { ProductMultiPicker } from '../components/ProductMultiPicker';
import { RichTextEditor } from '../components/RichTextEditor';
import { api, ApiError } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type {
  Brand,
  Category,
  CollectionRow,
  PageDetail,
  PageList,
  PostCategoryList,
  PreviewLink,
} from '../lib/types';
import { StorePicker, useStoreChoice } from './payments';
import { openInNewTab } from '../lib/open-tab';

type Kind = 'home' | 'page' | 'landing';
type SectionType =
  'hero' | 'richText' | 'imageText' | 'productGrid' | 'categoryTiles' | 'banners' | 'postList' | 'faq';
const SECTION_TYPES: SectionType[] = [
  'hero',
  'richText',
  'imageText',
  'productGrid',
  'categoryTiles',
  'banners',
  'postList',
  'faq',
];
const EMPTY_DOC = { type: 'doc', content: [] };
type S = SectionInput & { id: string };

const stateTone: Record<PageList[number]['state'], Tone> = {
  draft: 'neutral',
  scheduled: 'amber',
  live: 'moss',
  expired: 'slate',
  unpublished: 'slate',
};

function stable(v: unknown): string {
  return JSON.stringify(v, (_k, x: unknown) =>
    x && typeof x === 'object' && !Array.isArray(x)
      ? Object.fromEntries(
          Object.entries(x as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b)),
        )
      : x,
  );
}

// ---------------------------------------------------------------------------- Liste

/** Sayfalar: ana sayfa düzeni, kurumsal ve kampanya sayfaları. */
export function PagesPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const { stores, loading, storeId, setStoreId } = useStoreChoice();
  const { locales, locale, setLocale } = useContentLocale(storeId);
  const [creating, setCreating] = useState(false);
  const list = useQuery({
    queryKey: ['pages', storeId, locale],
    queryFn: () => api.get<PageList>(`/v1/admin/pages?storeId=${storeId}&locale=${locale}`),
    enabled: !!storeId && !!locale,
  });
  if (loading) return <Spinner label={t('common.loading')} />;
  const hasHome = !!list.data?.some((p) => p.kind === 'home');
  return (
    <PageFrame
      helpKey="page.pages"
      actions={
        can('content.write') &&
        storeId && (
          <div className="flex items-center gap-2">
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" aria-hidden />
              {t('admin.pages.create')}
            </Button>
            <HelpTip k="pages.create" />
          </div>
        )
      }
    >
      {!stores.length ? (
        <EmptyState title={t('admin.payments.noStore')} description={t('admin.payments.noStoreLead')} />
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-4">
            <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
            <LocalePicker locales={locales} locale={locale} onChange={setLocale} />
          </div>
          {!list.data ? (
            <Spinner label={t('common.loading')} />
          ) : !list.data.length ? (
            <EmptyState title={t('admin.pages.empty')} description={t('admin.pages.emptyLead')} />
          ) : (
            <ul className="divide-y divide-line rounded-md border border-line bg-surface">
              {list.data.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-sm">
                  <div className="min-w-0 flex-1">
                    <Link
                      to="/sayfalar/$id"
                      params={{ id: p.id }}
                      className="font-medium text-pine hover:underline"
                    >
                      {p.title}
                    </Link>
                    <p className="text-ink-3">
                      {t(`admin.pages.kinds.${p.kind}`)} · {p.kind === 'home' ? '/' : `/sayfa/${p.slug}`} ·{' '}
                      {t('admin.pages.updated', { date: formatDate(p.updatedAt, true) })}
                    </p>
                    <TranslationLinks
                      items={p.translations}
                      to="/sayfalar/$id"
                      stateLabel={(s) => t(`admin.pages.states.${s}`)}
                    />
                  </div>
                  <Badge tone={stateTone[p.state]}>{t(`admin.pages.states.${p.state}`)}</Badge>
                  {p.reviewStatus !== 'not_required' && (
                    <Badge tone={reviewTone[p.reviewStatus]}>
                      {t(`admin.pages.reviews.${p.reviewStatus}`)}
                    </Badge>
                  )}
                </li>
              ))}
            </ul>
          )}
          {locale && (
            <CreatePageDialog
              open={creating}
              onOpenChange={setCreating}
              storeId={storeId}
              locale={locale}
              hasHome={hasHome}
            />
          )}
        </>
      )}
    </PageFrame>
  );
}

function CreatePageDialog({
  open,
  onOpenChange,
  storeId,
  locale,
  hasHome,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  storeId: string;
  locale: string;
  hasHome: boolean;
}) {
  const t = useT();
  const navigate = useNavigate();
  const [kind, setKind] = useState<Kind>(hasHome ? 'page' : 'home');
  const [title, setTitle] = useState('');
  useEffect(() => {
    if (open) {
      setKind(hasHome ? 'page' : 'home');
      setTitle('');
    }
  }, [open, hasHome]);
  const create = useApiMutation(
    () => api.post<{ id: string }>('/v1/admin/pages', { storeId, kind, title, locale }),
    {
      invalidate: [['pages', storeId]],
      onSuccess: (r) => {
        onOpenChange(false);
        void navigate({ to: '/sayfalar/$id', params: { id: r.id } });
      },
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('admin.pages.create')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            disabled={!title.trim() || create.isPending}
            onClick={() => create.mutate(undefined)}
          >
            {t('admin.pages.createConfirm')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label={t('admin.pages.kind')} help={<HelpTip k="pages.kind" />}>
          {(p) => (
            <Select {...p} value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
              {(['home', 'page', 'landing'] as const)
                .filter((k) => k !== 'home' || !hasHome)
                .map((k) => (
                  <option key={k} value={k}>
                    {t(`admin.pages.kinds.${k}`)}
                  </option>
                ))}
            </Select>
          )}
        </Field>
        <Field
          label={t('admin.pages.title')}
          help={<HelpTip k="pages.title" />}
          error={fieldError(create.error, 'title')}
        >
          {(p) => <Input {...p} value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} />}
        </Field>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------- Düzenleyici

interface Draft {
  title: string;
  slug: string;
  seoTitle: string;
  seoDescription: string;
  healthContent: boolean;
  sections: S[];
}

function draftOf(p: PageDetail): Draft {
  return {
    title: p.title,
    slug: p.slug,
    seoTitle: p.seoTitle ?? '',
    seoDescription: p.seoDescription ?? '',
    healthContent: p.healthContent,
    sections: p.sections as S[],
  };
}

const newSection = (type: SectionType, t: (k: string) => string): S => {
  const id = crypto.randomUUID().slice(0, 12);
  switch (type) {
    case 'hero':
      return { id, type, heading: t('admin.pages.newHeading') };
    case 'richText':
      return { id, type, doc: EMPTY_DOC };
    case 'imageText':
      return { id, type, image: { fileId: '', alt: '' }, doc: EMPTY_DOC, imagePosition: 'start' };
    case 'productGrid':
      return { id, type, source: 'newest', limit: 8 };
    case 'categoryTiles':
      return { id, type, categoryIds: [] };
    case 'banners':
      return { id, type, placement: 'home_hero' };
    case 'postList':
      return { id, type, heading: null, categoryId: null, limit: 3 };
    case 'faq':
      return { id, type, items: [{ question: '', answer: EMPTY_DOC }] };
  }
};

export function PageEditorPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const { id } = useParams({ from: '/app/sayfalar/$id' });
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canWrite = can('content.write');
  const canPublish = can('content.publish');
  const q = useQuery({ queryKey: ['page', id], queryFn: () => api.get<PageDetail>(`/v1/admin/pages/${id}`) });
  const cats = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<Category[]>('/v1/admin/categories'),
    enabled: can('catalog.product.read'),
  });
  const postCats = useQuery({
    queryKey: ['post-categories', q.data?.storeId, q.data?.locale],
    queryFn: () =>
      api.get<PostCategoryList>(
        `/v1/admin/post-categories?storeId=${q.data!.storeId}&locale=${q.data!.locale}`,
      ),
    enabled: !!q.data,
  });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [newType, setNewType] = useState<SectionType>('richText');
  useEffect(() => {
    if (q.data) setDraft(draftOf(q.data));
  }, [q.data]);
  const page = q.data;
  const dirty = !!page && !!draft && stable(draft) !== stable(draftOf(page));
  const body = () => ({
    title: draft!.title,
    ...(page!.kind === 'home' ? {} : { slug: draft!.slug }),
    seoTitle: draft!.seoTitle || null,
    seoDescription: draft!.seoDescription || null,
    healthContent: draft!.healthContent,
    sections: draft!.sections,
    version: page!.version,
  });
  const save = useApiMutation(() => api.put<PageDetail>(`/v1/admin/pages/${id}`, body()), {
    invalidate: [['page', id]],
    success: t('admin.pages.saved'),
  });
  const ensureSaved = async (): Promise<number> => {
    if (!dirty) return page!.version;
    const r = await api.put<PageDetail>(`/v1/admin/pages/${id}`, body());
    return r.version;
  };
  const preview = useApiMutation(
    async () => {
      await ensureSaved();
      return api.post<PreviewLink>(`/v1/admin/pages/${id}/preview-link`);
    },
    {
      invalidate: [['page', id]],
      onSuccess: (r) => {
        if (!openInNewTab(r.url)) toast.message(t('admin.design.previewBlocked'), { description: r.url });
      },
    },
  );
  const publish = useApiMutation(
    async (v: { publishAt: string; unpublishAt: string; note: string }) => {
      const version = await ensureSaved();
      return api.post<PageDetail>(`/v1/admin/pages/${id}/publish`, {
        publishAt: v.publishAt ? new Date(v.publishAt).toISOString() : null,
        unpublishAt: v.unpublishAt ? new Date(v.unpublishAt).toISOString() : null,
        note: v.note || null,
        version,
      });
    },
    {
      invalidate: [['page', id], ['pages']],
      success: t('admin.pages.publishedToast'),
      onSuccess: () => setPublishing(false),
    },
  );
  const unpublish = useApiMutation(
    () => api.post<PageDetail>(`/v1/admin/pages/${id}/unpublish`, { version: page!.version }),
    { invalidate: [['page', id], ['pages']], success: t('admin.pages.unpublishedToast') },
  );
  const review = useApiMutation(
    (v: { decision: 'approved' | 'rejected'; note: string }) =>
      api.post<PageDetail>(`/v1/admin/pages/${id}/review`, {
        decision: v.decision,
        note: v.note || null,
        contentHash: page!.contentHash,
        version: page!.version,
      }),
    { invalidate: [['page', id], ['pages']], success: t('admin.pages.reviewedToast') },
  );
  const remove = useApiMutation(() => api.del(`/v1/admin/pages/${id}`), {
    invalidate: [['pages']],
    success: t('common.deleted'),
    onSuccess: () => void navigate({ to: '/sayfalar' }),
  });
  const translate = useApiMutation(
    (locale: string) => api.post<PageDetail>(`/v1/admin/pages/${id}/translations`, { locale }),
    {
      invalidate: [['pages'], ['page', id]],
      success: t('admin.translations.created'),
      silentFieldErrors: true,
      onSuccess: (r) => void navigate({ to: '/sayfalar/$id', params: { id: r.id } }),
    },
  );
  if (!page || !draft) return <Spinner label={t('common.loading')} />;
  const set = (patch: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...patch } : d));
  const setSection = (i: number, s: S | null) =>
    set({
      sections: s
        ? draft.sections.map((x, j) => (j === i ? s : x))
        : draft.sections.filter((_, j) => j !== i),
    });
  const moveSection = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= draft.sections.length) return;
    const copy = [...draft.sections];
    [copy[i], copy[j]] = [copy[j]!, copy[i]!];
    set({ sections: copy });
  };
  const err = save.error ?? publish.error;
  // Yalnızca bölümlerin tümüyle ilgili hata (ör. eksik dosya); bölüm içi hatalar kendi alanında gösterilir.
  const sectionsError =
    err instanceof ApiError ? err.errors.find((e) => e.field === 'sections')?.message : undefined;
  return (
    <PageFrame
      helpKey="page.page_editor"
      title={page.title}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {canWrite && (
            <>
              <Button onClick={() => preview.mutate(undefined)} disabled={preview.isPending}>
                <ExternalLink className="h-4 w-4" aria-hidden />
                {t('admin.pages.preview')}
              </Button>
              <HelpTip k="pages.preview" />
              <Button onClick={() => save.mutate(undefined)} disabled={!dirty || save.isPending}>
                {t('admin.pages.save')}
              </Button>
              <HelpTip k="pages.save" />
            </>
          )}
          {canPublish && (
            <>
              <Button variant="primary" onClick={() => setPublishing(true)}>
                {t('admin.pages.publish')}
              </Button>
              <HelpTip k="pages.publish" />
            </>
          )}
        </div>
      }
    >
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Link to="/sayfalar" className="text-pine hover:underline">
          {t('admin.pages.back')}
        </Link>
        <Badge tone={stateTone[page.state]}>{t(`admin.pages.states.${page.state}`)}</Badge>
        {page.publishedVersion && (
          <Badge tone="neutral">{t('admin.pages.liveVersion', { version: page.publishedVersion })}</Badge>
        )}
        {(dirty || page.hasUnpublishedChanges) && <Badge tone="amber">{t('admin.pages.unpublished')}</Badge>}
        {page.publishAt && (
          <span className="text-ink-2">
            {t('admin.pages.publishAtInfo', { date: formatDate(page.publishAt, true) })}
          </span>
        )}
        {page.unpublishAt && (
          <span className="text-ink-2">
            {t('admin.pages.unpublishAtInfo', { date: formatDate(page.unpublishAt, true) })}
          </span>
        )}
      </div>
      <EditorLanguages
        storeId={page.storeId}
        locale={page.locale}
        translations={page.translations}
        to="/sayfalar/$id"
        stateLabel={(s) => t(`admin.pages.states.${s}`)}
        canTranslate={canWrite}
        pending={translate.isPending}
        error={fieldError(translate.error, 'locale') ?? fieldError(translate.error, 'slug')}
        onCreate={(l) => translate.mutate(l)}
      />

      <fieldset disabled={!canWrite} className="space-y-8">
        <Section title={t('admin.pages.basics')} className="border-t-0 pt-0">
          <div className="grid gap-4 md:grid-cols-2">
            <Field
              label={t('admin.pages.title')}
              help={<HelpTip k="pages.title" />}
              error={fieldError(err, 'title')}
            >
              {(p) => (
                <Input
                  {...p}
                  value={draft.title}
                  maxLength={120}
                  onChange={(e) => set({ title: e.target.value })}
                />
              )}
            </Field>
            {page.kind !== 'home' && (
              <Field
                label={t('admin.pages.slug')}
                help={<HelpTip k="pages.slug" />}
                description={t('admin.pages.slugLead', { path: `/sayfa/${draft.slug}` })}
                error={fieldError(err, 'slug')}
              >
                {(p) => (
                  <Input
                    {...p}
                    value={draft.slug}
                    maxLength={80}
                    spellCheck={false}
                    disabled={!!page.publishedVersion && !canPublish}
                    onChange={(e) => set({ slug: e.target.value.toLowerCase() })}
                  />
                )}
              </Field>
            )}
            <Field
              label={t('admin.pages.seoTitle')}
              help={<HelpTip k="pages.seo_title" />}
              optionalLabel={t('common.optional')}
              description={t('admin.pages.chars', { count: draft.seoTitle.length, max: 70 })}
            >
              {(p) => (
                <Input
                  {...p}
                  value={draft.seoTitle}
                  maxLength={70}
                  onChange={(e) => set({ seoTitle: e.target.value })}
                />
              )}
            </Field>
            <Field
              label={t('admin.pages.seoDescription')}
              help={<HelpTip k="pages.seo_description" />}
              optionalLabel={t('common.optional')}
              description={t('admin.pages.chars', { count: draft.seoDescription.length, max: 170 })}
            >
              {(p) => (
                <Textarea
                  {...p}
                  value={draft.seoDescription}
                  maxLength={170}
                  onChange={(e) => set({ seoDescription: e.target.value })}
                />
              )}
            </Field>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Switch
              id="page-health"
              checked={draft.healthContent}
              onCheckedChange={(v) => set({ healthContent: v })}
              label={t('admin.pages.healthContent')}
            />
            <label htmlFor="page-health" className="text-sm font-medium">
              {t('admin.pages.healthContent')}
            </label>
            <HelpTip k="pages.health_content" />
          </div>
        </Section>

        <Section title={t('admin.pages.sections')} description={t('admin.pages.sectionsLead')}>
          {sectionsError && (
            <p role="alert" className="mb-3 text-sm font-medium text-rose">
              {sectionsError}
            </p>
          )}
          {!draft.sections.length && <EmptyState title={t('admin.pages.noSections')} />}
          <ol className="space-y-4">
            {draft.sections.map((s, i) => (
              <li key={s.id} className="rounded-md border border-line bg-surface p-4">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span className="font-cond text-base font-semibold">
                    {i + 1}. {t(`admin.pages.sectionTypes.${s.type}`)}
                  </span>
                  {s.hidden && <Badge tone="slate">{t('admin.pages.hidden')}</Badge>}
                  <span className="ms-auto flex flex-wrap items-center gap-1">
                    <label className="flex items-center gap-1.5 text-sm">
                      <input
                        type="checkbox"
                        checked={!!s.hidden}
                        onChange={(e) => setSection(i, { ...s, hidden: e.target.checked })}
                        className="h-4 w-4"
                      />
                      {t('admin.pages.hide')}
                    </label>
                    <HelpTip k="pages.section_hide" />
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={i === 0}
                      onClick={() => moveSection(i, -1)}
                      aria-label={t('admin.pages.moveUp', { n: i + 1 })}
                    >
                      <ArrowUp className="h-4 w-4" aria-hidden />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={i === draft.sections.length - 1}
                      onClick={() => moveSection(i, 1)}
                      aria-label={t('admin.pages.moveDown', { n: i + 1 })}
                    >
                      <ArrowDown className="h-4 w-4" aria-hidden />
                    </Button>
                    <HelpTip k="pages.section_move" />
                    <Button size="sm" variant="danger" onClick={() => setSection(i, null)}>
                      {t('common.remove')}
                    </Button>
                    <HelpTip k="pages.section_remove" />
                  </span>
                </div>
                <SectionFields
                  section={s}
                  path={`sections.${i}`}
                  error={err}
                  categories={cats.data ?? []}
                  postCategories={postCats.data ?? []}
                  onChange={(next) => setSection(i, next)}
                />
              </li>
            ))}
          </ol>
          {canWrite && (
            <div className="mt-4 flex flex-wrap items-end gap-2">
              <Field
                label={t('admin.pages.sectionType')}
                help={<HelpTip k="pages.section_add" />}
                className="w-60"
              >
                {(p) => (
                  <Select {...p} value={newType} onChange={(e) => setNewType(e.target.value as SectionType)}>
                    {SECTION_TYPES.map((x) => (
                      <option key={x} value={x}>
                        {t(`admin.pages.sectionTypes.${x}`)}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Button onClick={() => set({ sections: [...draft.sections, newSection(newType, t)] })}>
                <Plus className="h-4 w-4" aria-hidden />
                {t('admin.pages.addSection')}
              </Button>
              <HelpTip k="pages.add_section" />
            </div>
          )}
        </Section>
      </fieldset>

      <ContentReviewPanel
        status={page.reviewStatus}
        note={page.reviewNote}
        findings={page.findings}
        required={page.requiresReview}
        dirty={dirty}
        canReview={can('content.expert_review')}
        busy={review.isPending}
        onDecide={(decision, note) => review.mutate({ decision, note })}
      />

      <Section title={t('admin.pages.versions')} description={t('admin.pages.versionsLead')}>
        {!page.versions.length ? (
          <p className="text-sm text-ink-2">{t('admin.pages.noVersions')}</p>
        ) : (
          <ul className="divide-y divide-line rounded-md border border-line bg-surface">
            {page.versions.map((v) => (
              <li key={v.version} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-sm">
                <span className="font-medium">{t('admin.design.versionNo', { version: v.version })}</span>
                <span className="text-ink-2">
                  {formatDate(v.publishedAt, true)}
                  {v.publishedBy ? ` · ${v.publishedBy}` : ''}
                </span>
                {v.current && <Badge tone="moss">{t('admin.design.current')}</Badge>}
                {v.note && <span className="text-ink-2">{v.note}</span>}
              </li>
            ))}
          </ul>
        )}
        {canPublish && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {page.status === 'published' && (
              <>
                <ConfirmButton
                  variant="secondary"
                  message={t('admin.pages.unpublishConfirm')}
                  onConfirm={() => unpublish.mutateAsync(undefined)}
                >
                  {t('admin.pages.unpublish')}
                </ConfirmButton>
                <HelpTip k="pages.unpublish" />
              </>
            )}
            {!page.publishedVersion && (
              <>
                <ConfirmButton
                  message={t('admin.pages.deleteConfirm')}
                  onConfirm={() => remove.mutateAsync(undefined)}
                >
                  {t('common.delete')}
                </ConfirmButton>
                <HelpTip k="pages.delete" />
              </>
            )}
          </div>
        )}
      </Section>

      <PublishPageDialog
        open={publishing}
        onOpenChange={setPublishing}
        busy={publish.isPending}
        error={publish.error}
        dirty={dirty}
        onPublish={(v) => publish.mutate(v)}
      />
    </PageFrame>
  );
}

function PublishPageDialog({
  open,
  onOpenChange,
  busy,
  error,
  dirty,
  onPublish,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  busy: boolean;
  error: unknown;
  dirty: boolean;
  onPublish: (v: { publishAt: string; unpublishAt: string; note: string }) => void;
}) {
  const t = useT();
  const [publishAt, setPublishAt] = useState('');
  const [unpublishAt, setUnpublishAt] = useState('');
  const [note, setNote] = useState('');
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('admin.pages.publishTitle')}
      description={t('admin.pages.publishLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            disabled={busy}
            onClick={() => onPublish({ publishAt, unpublishAt, note: note.trim() })}
          >
            {t('admin.pages.publishConfirm')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {dirty && <p className="text-sm text-ink-2">{t('admin.design.publishSavesFirst')}</p>}
        {error instanceof ApiError && !error.errors.length && (
          <p role="alert" className="text-sm font-medium text-rose">
            {error.title}
          </p>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label={t('admin.pages.publishAt')}
            optionalLabel={t('common.optional')}
            help={<HelpTip k="pages.schedule" />}
          >
            {(p) => (
              <Input
                {...p}
                type="datetime-local"
                value={publishAt}
                onChange={(e) => setPublishAt(e.target.value)}
              />
            )}
          </Field>
          <Field
            label={t('admin.pages.unpublishAt')}
            optionalLabel={t('common.optional')}
            help={<HelpTip k="pages.schedule" />}
            error={fieldError(error, 'unpublishAt')}
          >
            {(p) => (
              <Input
                {...p}
                type="datetime-local"
                value={unpublishAt}
                onChange={(e) => setUnpublishAt(e.target.value)}
              />
            )}
          </Field>
        </div>
        <Field
          label={t('admin.design.note')}
          optionalLabel={t('common.optional')}
          help={<HelpTip k="design.note" />}
        >
          {(p) => <Textarea {...p} value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} />}
        </Field>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------- Bölüm alanları

/** Ürün ızgarası kaynağı için koleksiyon veya marka seçimi (F5B-04). */
function RefSelect({
  kind,
  value,
  error,
  onChange,
}: {
  kind: 'collection' | 'brand';
  value: string;
  error?: string;
  onChange: (id: string) => void;
}) {
  const t = useT();
  const cols = useQuery({
    queryKey: ['collections'],
    queryFn: () => api.get<CollectionRow[]>('/v1/admin/collections'),
    enabled: kind === 'collection',
  });
  const brs = useQuery({
    queryKey: ['brands'],
    queryFn: () => api.get<Brand[]>('/v1/admin/brands'),
    enabled: kind === 'brand',
  });
  const rows =
    kind === 'collection'
      ? (cols.data ?? []).map((r) => ({ id: r.id, label: r.title, off: r.status !== 'active' }))
      : (brs.data ?? []).map((r) => ({ id: r.id, label: r.name, off: r.status !== 'active' }));
  return (
    <Field
      label={t(kind === 'collection' ? 'admin.pages.gridCollection' : 'admin.pages.gridBrand')}
      help={<HelpTip k={kind === 'collection' ? 'pages.grid_collection' : 'pages.grid_brand'} />}
      error={error}
    >
      {(p) => (
        <Select {...p} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">{t('admin.pages.chooseRef')}</option>
          {rows.map((r) => (
            <option key={r.id} value={r.id}>
              {r.off ? t('admin.pages.refInactive', { name: r.label }) : r.label}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
}

function SectionFields({
  section: s,
  path,
  error,
  categories,
  postCategories,
  onChange,
}: {
  section: S;
  path: string;
  error: unknown;
  categories: Category[];
  postCategories: PostCategoryList;
  onChange: (s: S) => void;
}) {
  const t = useT();
  const editorId = useId();
  const fe = (f: string) => fieldError(error, `${path}.${f}`);
  switch (s.type) {
    case 'hero':
      return (
        <div className="grid gap-3 md:grid-cols-2">
          <Field label={t('admin.pages.heading')} help={<HelpTip k="pages.heading" />} error={fe('heading')}>
            {(p) => (
              <Input
                {...p}
                value={s.heading}
                maxLength={120}
                onChange={(e) => onChange({ ...s, heading: e.target.value })}
              />
            )}
          </Field>
          <Field
            label={t('admin.pages.heroText')}
            optionalLabel={t('common.optional')}
            help={<HelpTip k="pages.hero_text" />}
            error={fe('text')}
          >
            {(p) => (
              <Input
                {...p}
                value={s.text ?? ''}
                maxLength={300}
                onChange={(e) => onChange({ ...s, text: e.target.value })}
              />
            )}
          </Field>
          <ImageField
            value={s.image ?? null}
            optional
            error={fe('image')}
            onChange={(image) => onChange({ ...s, image })}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label={t('admin.pages.ctaLabel')}
              optionalLabel={t('common.optional')}
              help={<HelpTip k="pages.cta" />}
            >
              {(p) => (
                <Input
                  {...p}
                  value={s.cta?.label ?? ''}
                  maxLength={40}
                  onChange={(e) =>
                    onChange({
                      ...s,
                      cta:
                        e.target.value || s.cta?.href
                          ? { label: e.target.value, href: s.cta?.href ?? '' }
                          : null,
                    })
                  }
                />
              )}
            </Field>
            <Field
              label={t('admin.pages.ctaHref')}
              optionalLabel={t('common.optional')}
              help={<HelpTip k="pages.cta" />}
              error={fe('cta')}
            >
              {(p) => (
                <Input
                  {...p}
                  value={s.cta?.href ?? ''}
                  placeholder="/urunler"
                  spellCheck={false}
                  onChange={(e) =>
                    onChange({
                      ...s,
                      cta:
                        e.target.value || s.cta?.label
                          ? { label: s.cta?.label ?? '', href: e.target.value }
                          : null,
                    })
                  }
                />
              )}
            </Field>
          </div>
        </div>
      );
    case 'richText':
      return (
        <>
          <RichTextEditor
            id={editorId}
            label={t('admin.pages.body')}
            value={s.doc}
            help={<HelpTip k="pages.rich_text" />}
            onChange={(doc) => onChange({ ...s, doc })}
          />
          {fe('doc') && (
            <p role="alert" className="mt-1 text-sm font-medium text-rose">
              {fe('doc')}
            </p>
          )}
        </>
      );
    case 'imageText':
      return (
        <div className="grid gap-3">
          <div className="grid gap-3 md:grid-cols-2">
            <Field
              label={t('admin.pages.heading')}
              optionalLabel={t('common.optional')}
              help={<HelpTip k="pages.heading" />}
            >
              {(p) => (
                <Input
                  {...p}
                  value={s.heading ?? ''}
                  maxLength={120}
                  onChange={(e) => onChange({ ...s, heading: e.target.value })}
                />
              )}
            </Field>
            <Field label={t('admin.pages.imagePosition')} help={<HelpTip k="pages.image_position" />}>
              {(p) => (
                <Select
                  {...p}
                  value={s.imagePosition ?? 'start'}
                  onChange={(e) => onChange({ ...s, imagePosition: e.target.value as 'start' | 'end' })}
                >
                  <option value="start">{t('admin.design.galleryPositions.start')}</option>
                  <option value="end">{t('admin.design.galleryPositions.end')}</option>
                </Select>
              )}
            </Field>
          </div>
          <ImageField
            value={s.image.fileId ? s.image : null}
            error={fe('image')}
            onChange={(image) => onChange({ ...s, image: image ?? { fileId: '', alt: '' } })}
          />
          <RichTextEditor
            id={editorId}
            label={t('admin.pages.body')}
            value={s.doc}
            help={<HelpTip k="pages.rich_text" />}
            onChange={(doc) => onChange({ ...s, doc })}
          />
        </div>
      );
    case 'productGrid':
      return (
        <div className="grid gap-3 md:grid-cols-2">
          <Field
            label={t('admin.pages.heading')}
            optionalLabel={t('common.optional')}
            help={<HelpTip k="pages.heading" />}
          >
            {(p) => (
              <Input
                {...p}
                value={s.heading ?? ''}
                maxLength={120}
                onChange={(e) => onChange({ ...s, heading: e.target.value })}
              />
            )}
          </Field>
          <Field label={t('admin.pages.gridSource')} help={<HelpTip k="pages.grid_source" />}>
            {(p) => (
              <Select
                {...p}
                value={s.source}
                onChange={(e) => onChange({ ...s, source: e.target.value as typeof s.source })}
              >
                {(
                  [
                    'newest',
                    'bestsellers',
                    'discounted',
                    'category',
                    'collection',
                    'brand',
                    'products',
                  ] as const
                ).map((x) => (
                  <option key={x} value={x}>
                    {t(`admin.pages.gridSources.${x}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {s.source === 'category' && (
            <Field
              label={t('admin.menus.category')}
              help={<HelpTip k="pages.grid_category" />}
              error={fe('categoryId')}
            >
              {(p) => (
                <Select
                  {...p}
                  value={s.categoryId ?? ''}
                  onChange={(e) => onChange({ ...s, categoryId: e.target.value || null })}
                >
                  <option value="">{t('admin.menus.chooseCategory')}</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
          {s.source === 'collection' && (
            <RefSelect
              kind="collection"
              value={s.collectionId ?? ''}
              error={fe('collectionId')}
              onChange={(collectionId) => onChange({ ...s, collectionId: collectionId || null })}
            />
          )}
          {s.source === 'brand' && (
            <RefSelect
              kind="brand"
              value={s.brandId ?? ''}
              error={fe('brandId')}
              onChange={(brandId) => onChange({ ...s, brandId: brandId || null })}
            />
          )}
          {s.source !== 'products' && (
            <Field label={t('admin.pages.gridLimit')} help={<HelpTip k="pages.grid_limit" />}>
              {(p) => (
                <Input
                  {...p}
                  type="number"
                  min={2}
                  max={24}
                  value={s.limit ?? 8}
                  onChange={(e) =>
                    onChange({ ...s, limit: Math.max(2, Math.min(24, Number(e.target.value) || 8)) })
                  }
                />
              )}
            </Field>
          )}
          {s.source === 'products' && (
            <div className="md:col-span-2">
              <ProductMultiPicker
                value={s.productIds ?? []}
                label={t('admin.pages.products')}
                help={<HelpTip k="pages.products" />}
                max={24}
                error={fe('productIds')}
                onChange={(productIds) => onChange({ ...s, productIds })}
              />
            </div>
          )}
        </div>
      );
    case 'categoryTiles':
      return (
        <div className="grid gap-3">
          <Field
            label={t('admin.pages.heading')}
            optionalLabel={t('common.optional')}
            help={<HelpTip k="pages.heading" />}
          >
            {(p) => (
              <Input
                {...p}
                value={s.heading ?? ''}
                maxLength={120}
                onChange={(e) => onChange({ ...s, heading: e.target.value })}
              />
            )}
          </Field>
          <fieldset>
            <legend className="mb-1.5 flex items-center gap-1.5 text-sm font-medium">
              {t('admin.pages.tileCategories')}
              <HelpTip k="pages.tiles" />
            </legend>
            {fe('categoryIds') && (
              <p role="alert" className="mb-1 text-sm font-medium text-rose">
                {fe('categoryIds')}
              </p>
            )}
            <div className="grid gap-1 sm:grid-cols-2 md:grid-cols-3">
              {categories.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={s.categoryIds.includes(c.id)}
                    onChange={(e) =>
                      onChange({
                        ...s,
                        categoryIds: e.target.checked
                          ? [...s.categoryIds, c.id]
                          : s.categoryIds.filter((x) => x !== c.id),
                      })
                    }
                  />
                  {c.name}
                </label>
              ))}
            </div>
          </fieldset>
        </div>
      );
    case 'faq':
      return (
        <div className="grid gap-4">
          <Field
            label={t('admin.pages.heading')}
            optionalLabel={t('common.optional')}
            help={<HelpTip k="pages.heading" />}
          >
            {(p) => (
              <Input
                {...p}
                value={s.heading ?? ''}
                maxLength={120}
                onChange={(e) => onChange({ ...s, heading: e.target.value })}
              />
            )}
          </Field>
          {s.items.map((it, j) => (
            <div key={j} className="grid gap-2 rounded-sm border border-line p-3">
              <Field
                label={t('admin.pages.question', { n: j + 1 })}
                help={<HelpTip k="pages.faq" />}
                error={fe(`items.${j}.question`)}
              >
                {(p) => (
                  <Input
                    {...p}
                    value={it.question}
                    maxLength={300}
                    onChange={(e) =>
                      onChange({
                        ...s,
                        items: s.items.map((x, k) => (k === j ? { ...x, question: e.target.value } : x)),
                      })
                    }
                  />
                )}
              </Field>
              <FaqAnswer
                value={it.answer}
                label={t('admin.pages.answer', { n: j + 1 })}
                onChange={(answer) =>
                  onChange({ ...s, items: s.items.map((x, k) => (k === j ? { ...x, answer } : x)) })
                }
              />
              {s.items.length > 1 && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="justify-self-start"
                  onClick={() => onChange({ ...s, items: s.items.filter((_, k) => k !== j) })}
                >
                  {t('admin.pages.removeQuestion')}
                </Button>
              )}
            </div>
          ))}
          <Button
            size="sm"
            className="justify-self-start"
            onClick={() => onChange({ ...s, items: [...s.items, { question: '', answer: EMPTY_DOC }] })}
          >
            <Plus className="h-4 w-4" aria-hidden />
            {t('admin.pages.addQuestion')}
          </Button>
          <HelpTip k="pages.faq_items" />
        </div>
      );
    case 'postList':
      return (
        <div className="grid gap-3 md:grid-cols-3">
          <Field
            label={t('admin.pages.heading')}
            optionalLabel={t('common.optional')}
            help={<HelpTip k="pages.heading" />}
          >
            {(p) => (
              <Input
                {...p}
                value={s.heading ?? ''}
                maxLength={120}
                onChange={(e) => onChange({ ...s, heading: e.target.value })}
              />
            )}
          </Field>
          <Field
            label={t('admin.blog.category')}
            help={<HelpTip k="pages.post_list" />}
            error={fe('categoryId')}
          >
            {(p) => (
              <Select
                {...p}
                value={s.categoryId ?? ''}
                onChange={(e) => onChange({ ...s, categoryId: e.target.value || null })}
              >
                <option value="">{t('admin.pages.allPostCategories')}</option>
                {postCategories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('admin.pages.postLimit')} help={<HelpTip k="pages.post_limit" />}>
            {(p) => (
              <Input
                {...p}
                type="number"
                min={1}
                max={12}
                value={s.limit ?? 3}
                onChange={(e) =>
                  onChange({ ...s, limit: Math.max(1, Math.min(12, Number(e.target.value) || 3)) })
                }
              />
            )}
          </Field>
        </div>
      );
    case 'banners':
      return (
        <div className="grid gap-2 md:grid-cols-2">
          <Field label={t('admin.pages.bannerPlacement')} help={<HelpTip k="pages.banners" />}>
            {(p) => (
              <Select
                {...p}
                value={s.placement}
                onChange={(e) =>
                  onChange({ ...s, placement: e.target.value as 'home_hero' | 'home_secondary' })
                }
              >
                {(['home_hero', 'home_secondary'] as const).map((x) => (
                  <option key={x} value={x}>
                    {t(`admin.banners.placements.${x}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <p className="self-end text-sm text-ink-2">
            {t('admin.pages.bannersLead')}{' '}
            <Link to="/bannerlar" className="text-pine underline">
              {t('admin.pages.manageBanners')}
            </Link>
          </p>
        </div>
      );
    default:
      return <p className="text-sm text-ink-3">{t('admin.pages.sectionUnavailable')}</p>;
  }
}

function FaqAnswer({
  value,
  label,
  onChange,
}: {
  value: unknown;
  label: string;
  onChange: (doc: unknown) => void;
}) {
  const id = useId();
  return <RichTextEditor id={id} label={label} value={value} onChange={onChange} />;
}

/** Görsel alanı: yükle, alternatif metin (zorunlu), kaldır. */
function ImageField({
  value,
  optional,
  error,
  onChange,
}: {
  value: { fileId: string; alt: string } | null;
  optional?: boolean;
  error?: string;
  onChange: (v: { fileId: string; alt: string } | null) => void;
}) {
  const t = useT();
  return (
    <div className="grid gap-2">
      <ImageUpload
        value={value?.fileId || null}
        removable={optional}
        help={<HelpTip k="pages.image" />}
        onChange={(fileId) => onChange(fileId ? { fileId, alt: value?.alt ?? '' } : null)}
      />
      {value?.fileId && (
        <Field
          label={t('admin.pages.imageAlt')}
          help={<HelpTip k="pages.image_alt" />}
          error={error}
          required
        >
          {(p) => (
            <Input
              {...p}
              value={value.alt}
              maxLength={300}
              onChange={(e) => onChange({ ...value, alt: e.target.value })}
            />
          )}
        </Field>
      )}
      {!value?.fileId && error && (
        <p role="alert" className="text-sm font-medium text-rose">
          {error}
        </p>
      )}
    </div>
  );
}
